// App
import type React from 'react';
import { useState } from 'react';

// Constellation
import { Button, Input } from '@zillow/constellation';

import { useGetSearch } from '../../hooks/queries/search';
import { useGetUser } from '../../hooks/queries/user';
import { useGetAddress } from '../../hooks/queries/address';

// @ts-ignore
import { augmentError } from '../../utils/errors';

import constants from '../../constants/ConstantsBundle';

import { useEmail, useGoToAddressItem, useSuggestions } from './hooks';

import {
  StyledAddressInputContainer,
  StyledButtonContainer,
  StyledColumnContainer,
  StyledDisclaimer,
  StyledEmailInputContainer,
  StyledInputError,
  StyledLoadingSpinner,
  StyledUnitInputContainer,
} from './StyledComponents';

import Autocomplete from '../Autocomplete';
import NoZestimateModal from '../NoZestimateModal';

// Types
import type AutocompleteAddress from '../../models/AutocompleteAddress';
import { emitContinueAboveFoldEvent } from '../../analytics/priceMyRentalEvents';

// const logger = getLogger('modules/PriceMyRentalSearchModule');

const PriceMyRentalSearchModule = ({
  fontColor = 'light',
  customTracking,
}: {
  fontColor?: string;
  customTracking?: () => void;
}) => {
  // react hooks
  const [addressInputValue, updateAddressInputValue] = useState('');
  const [unit, updateUnit] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [validAddressItem, updateValidAddressItem] = useState<AutocompleteAddress | null>(null);

  // custom hooks
  const { data: user } = useGetUser();
  const { loggedIn: isLoggedIn } = user;

  const { data: recentSearches = [], addRecentSearch } = useGetSearch();

  const { setAddress } = useGetAddress();

  const { email, updateEmail, validateEmail, emailError } = useEmail();
  const { addressError, isNoZestModalOpen, openNoZestModal, updateAddressError, validateAndGoToAddressItem } =
    useGoToAddressItem();
  const { suggestions, getSuggestions } = useSuggestions(recentSearches as Array<AutocompleteAddress>);

  // action handlers
  const handleAddressChange = (newAddressInputValue: string) => {
    updateAddressInputValue(newAddressInputValue);
    getSuggestions(newAddressInputValue);
  };
  const handleAddressSelect = (addressItem: AutocompleteAddress) => {
    // update input value when selection is made
    updateAddressInputValue(addressItem.name || '');
    // reset address input error
    updateAddressError('');
    updateValidAddressItem(addressItem);
  };

  const handleNoZestModalClose = () => {
    openNoZestModal(false);
  };

  const handleEmailKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.which === constants.ENTER_KEYCODE) {
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    analyticsTracking();

    validateAndGoToAddressItem({
      addressInputValue,
      isLoggedIn,
      email,
      setIsLoading,
      unit,
      validAddressItem,
      validateEmail,
      addRecentSearch,
      setAddress,
    }).catch((err: Error) => {
      augmentError(err, {
        root: 'modules/PriceMyRentalSearchModule#handleSubmit',
      });

      // logger.error(formatForLog(err), 'Landing page: Error loading address/comps from search module');
    });
  };

  const analyticsTracking = () => {
    if (customTracking) {
      customTracking();
    } else {
      emitContinueAboveFoldEvent({ user });
    }
  };

  return (
    <div data-testid="PriceMyRentalSearchModule">
      <StyledAddressInputContainer>
        <Autocomplete
          items={suggestions}
          onChange={handleAddressChange}
          onSelect={handleAddressSelect}
          placeholder="Street address"
          value={addressInputValue}
        />

        <StyledUnitInputContainer>
          <form data-autocomplete="off">
            <Input
              onChange={(e: React.FormEvent<HTMLInputElement>) =>
                // @ts-ignore https://github.com/desktop/desktop/issues/297
                updateUnit(e.target.value)
              }
              placeholder="Unit"
            />
          </form>
        </StyledUnitInputContainer>
      </StyledAddressInputContainer>

      <StyledColumnContainer>
        {!isLoggedIn && (
          <StyledEmailInputContainer>
            <Input
              autoComplete="email"
              error={Boolean(emailError)}
              onChange={(e: React.FormEvent<HTMLInputElement>) =>
                // @ts-ignore https://github.com/desktop/desktop/issues/297
                updateEmail(e.target.value)
              }
              onKeyDown={handleEmailKeyDown}
              placeholder="Email address"
            />
          </StyledEmailInputContainer>
        )}

        {(addressError || emailError) && <StyledInputError>{addressError || emailError}</StyledInputError>}

        {!isLoggedIn && (
          <StyledDisclaimer fontColor={fontColor}>
            By providing your email address, you agree to receive promotional and marketing materials from Zillow.
          </StyledDisclaimer>
        )}
      </StyledColumnContainer>

      <NoZestimateModal address={addressInputValue} isOpen={isNoZestModalOpen} onClose={handleNoZestModalClose} />

      <StyledButtonContainer>
        <Button buttonType="primary" onClick={handleSubmit}>
          Continue
        </Button>

        {isLoading && <StyledLoadingSpinner size={'lg'} />}
      </StyledButtonContainer>
    </div>
  );
};

export default PriceMyRentalSearchModule;
